/**
 * InfinitSales - API Client
 * Base axios configuration for backend communication
 */
import axios from 'axios';
import { isDelegated, endDelegation } from '../../utils/delegation';
import { EMBED_MSG, isEmbedded, postToParent } from '../../utils/embed';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    // Evita a página interstitial do ngrok free em chamadas XHR quando o app
    // é servido por túnel (testes multi-máquina). Inócuo fora do ngrok.
    'ngrok-skip-browser-warning': 'true',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Sessão inválida (401 em chamada normal). Em vez de redirecionar na hora
// (sem aviso), o interceptor limpa o storage e dispara este evento; o
// AuthContext o ouve, marca `window.__infinitSessionInvalidHandled` /
// preventDefault e encerra a sessão com o toast "Sua sessão expirou". Se em
// SESSION_INVALID_FALLBACK_MS ninguém marcou (AuthContext não montado, ex.
// boot sem sessão), cai no comportamento antigo: reload para /login — ou,
// na sessão delegada, endDelegation + /empresa.
export const SESSION_INVALID_EVENT = 'infinitsales:session-invalid';
const SESSION_INVALID_FALLBACK_MS = 400;

/** Motivo do 401 vindo do backend ({"detail": {"code": "session_invalid", "reason"}} ou X-Session-Invalid). */
function sessionInvalidReason(response) {
  const detail = response?.data?.detail;
  if (detail && typeof detail === 'object' && detail.code === 'session_invalid' && detail.reason) {
    return detail.reason;
  }
  const header = response?.headers?.['x-session-invalid'];
  return header || 'unauthorized';
}

function fallbackRedirect(delegated) {
  if (isEmbedded()) {
    // CRM embutido: sem /login dentro do painel parceiro; ele renova a sessão.
    postToParent({ type: EMBED_MSG.EXPIRED, reason: 'api' });
    return;
  }
  if (delegated) {
    // Etapa 2.2: sessão delegada revogada (ator/alvo suspenso ou removido):
    // não deslogamos o usuário — restauramos o token real dele e voltamos
    // para a tela de workspaces.
    endDelegation();
    window.location.href = '/empresa';
    return;
  }
  window.location.href = '/login';
}

function notifySessionInvalid(reason, delegated) {
  window.__infinitSessionInvalidHandled = false;
  let event = null;
  try {
    event = new CustomEvent(SESSION_INVALID_EVENT, {
      detail: { reason, delegated },
      cancelable: true,
    });
    window.dispatchEvent(event);
  } catch (e) {
    // Sem CustomEvent (ambiente exótico): comportamento antigo.
    fallbackRedirect(delegated);
    return;
  }
  window.setTimeout(() => {
    const handled = event.defaultPrevented || window.__infinitSessionInvalidHandled === true;
    if (!handled) fallbackRedirect(delegated);
  }, SESSION_INVALID_FALLBACK_MS);
}

// Response interceptor - handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const delegated = isDelegated();
      // CRM embutido: o storage fica (o painel manda o token novo e a aba
      // continua no mesmo lugar); o AuthContext pede a renovação.
      if (!delegated && !isEmbedded()) {
        // Sessão normal: o token já não vale; some com ele antes de avisar.
        // (Na delegada o token real fica guardado e endDelegation o restaura.)
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
      notifySessionInvalid(sessionInvalidReason(error.response), delegated);
    }
    return Promise.reject(error);
  }
);

export default api;
